// Orchestrator for the redesigned Track Record "task" modal.
import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Box, CircularProgress, IconButton, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { ConfirmDialog, PDFPreview } from '@roc/ui';
import { useTrackRecordV2Styles } from './trackRecordV2.styles';
import { AnswerValue } from './trackRecordV2.config';
import { OverviewTab } from './overviewTab';
import { AddRecordsTab } from './addRecordsTab';
import { PropertiesTab, getPropertiesStatusCounts } from './propertiesTab';
import { EntitiesTab, getEntitiesStatusCounts } from './entitiesTab';
import { LicensesTab, getLicensesStatusCounts } from './licensesTab';
import { HasRecordsSidebar } from './hasRecordsSidebar';
import { TaskFooter } from './taskFooter';
import { ChatTab } from './chatTab';
import { AgentSearchBanner } from './agentSearchBanner';

export const TrackRecordV2 = observer(({
  onClose,
  borrowerName,
  token = '',
  store,
  borrowerId,
  taskId,
  isInternal,
  hideTaskTitle,
  hideTabs,
  maxHeight,
  onAccept,
  hideFooter,
}: {
  onClose?: () => void;
  borrowerName?: string;
  token?: string;
  store?: any;
  borrowerId?: number | string;
  taskId?: number;
  isInternal?: boolean;
  hideTaskTitle?: boolean;
  hideTabs?: boolean;
  maxHeight?: string | number;
  // Marks the track record task as completed; the footer's accept button only shows when provided.
  onAccept?: () => void;
  hideFooter?: boolean;
}) => {
  const classes = useTrackRecordV2Styles();
  const [selectedTab, setSelectedTab] = useState<'trackRecord' | 'chat'>('trackRecord');
  const [selectedNavItem, setSelectedNavItem] = useState('overview');
  const [isLoadingOverview, setIsLoadingOverview] = useState(true);
  const [confirmAcceptOpen, setConfirmAcceptOpen] = useState(false);
  const [loadedBorrowerId, setLoadedBorrowerId] = useState<number | string | null>(null);
  // Borrower for which the auto-save of previousExperience was already attempted (avoids retry loops on failure).
  const autoSavedBorrowerRef = useRef<number | string | null>(null);
  const verificationStore = store?.borrowerTrackRecordVerificationStore;
  const documentStore = store?.documentStore;
  const formStore = verificationStore?.trackRecordOtherFormStore;

  const answers: Record<string, AnswerValue> = {
    generalContractor: formStore?.form?.fields?.generalContractor?.value ?? null,
    broker: formStore?.form?.fields?.broker?.value ?? null,
    previousExperience: formStore?.form?.fields?.previousExperience?.value ?? null,
  };

  // Single request for everything this modal needs — properties, entities,
  // licenses, uploaded file, and borrowerInfo.
  useEffect(() => {
    if (!borrowerId || !verificationStore) {
      setIsLoadingOverview(false);
      return;
    }
    verificationStore.setCurrentToken(token);
    // Always reload on open — the singleton store may still hold the previous borrower's data.
    setIsLoadingOverview(true);
    Promise.resolve(verificationStore.loadFromOverview(borrowerId)).finally(() => {
      setLoadedBorrowerId(borrowerId);
      setIsLoadingOverview(false);
    });
  }, [borrowerId, verificationStore, token]);

  // Once the borrower has any real properties or entities, treat previousExperience
  // as answered even if the server hadn't explicitly recorded it.
  useEffect(() => {
    const hasTrackRecord = (verificationStore?.properties?.length ?? 0) > 0 || (verificationStore?.entities?.length ?? 0) > 0;
    // Only after this borrower's own data has loaded, so a previous borrower's records can't trigger a save.
    const isLoaded = !isLoadingOverview && loadedBorrowerId === borrowerId;
    if (
      isInternal && hasTrackRecord && isLoaded
      && autoSavedBorrowerRef.current !== borrowerId
      && formStore?.form?.fields?.previousExperience?.value !== 'Yes'
    ) {
      autoSavedBorrowerRef.current = borrowerId;
      verificationStore.saveEligibilityAnswer('previousExperience', 'Yes');
    }
  }, [verificationStore?.properties?.length, verificationStore?.entities?.length, formStore, isLoadingOverview, loadedBorrowerId, borrowerId, isInternal]);

  const propertiesCounts = getPropertiesStatusCounts(verificationStore);
  const entitiesCounts = getEntitiesStatusCounts(verificationStore);
  const licensesCounts = getLicensesStatusCounts(verificationStore);
  const sectionCounts = { properties: propertiesCounts, entities: entitiesCounts, licenses: licensesCounts };
  const remainingCount = propertiesCounts.needsInput + entitiesCounts.needsInput + licensesCounts.needsInput;

  const showFooter = !!isInternal && !hideFooter;

  // Dashboard switches save immediately.
  const handleAnswer = (key: string, value: AnswerValue) => {
    if (!formStore) return;
    if (verificationStore?.saveEligibilityAnswer) {
      verificationStore.saveEligibilityAnswer(key, value);
      return;
    }
    formStore.onFieldChange(key, value);
  };

  const handleConfirmAccept = () => {
    setConfirmAcceptOpen(false);
    onAccept?.();
  };

  return (
    <Box className={classes.root} style={maxHeight != null ? { height: maxHeight } : undefined}>
      <AgentSearchBanner
        classes={classes}
        pendingSearches={verificationStore?.pendingAgentSearches ?? []}
        onNavigate={(item) => {
          setSelectedTab('trackRecord');
          setSelectedNavItem(item);
        }}
        onDismiss={(id) => verificationStore?.dismissAgentSearch?.(id)}
      />
      {!hideTaskTitle && (
        <Box className={classes.headerRow}>
          <Typography variant="h5" className={classes.headerTitle}>
            Task
          </Typography>
          {onClose && (
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      )}

      {!hideTabs && (
        <Box className={classes.tabsRow}>
          <button
            type="button"
            className={`${classes.tab} ${selectedTab === 'trackRecord' ? classes.tabActive : ''}`}
            onClick={() => setSelectedTab('trackRecord')}
          >
            {borrowerName ? `${borrowerName} - ` : ''}Track Record
          </button>
          <button
            type="button"
            className={`${classes.tab} ${selectedTab === 'chat' ? classes.tabActive : ''}`}
            onClick={() => setSelectedTab('chat')}
          >
            Chat
          </button>
        </Box>
      )}

      <Box className={classes.scrollArea}>
        {selectedTab === 'chat' ? (
          <ChatTab classes={classes} taskId={taskId} />
        ) : isLoadingOverview ? (
          <Box display="flex" justifyContent="center" alignItems="center" style={{ height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box className={classes.dashboardContainer}>
            <HasRecordsSidebar
              classes={classes}
              selectedNavItem={selectedNavItem}
              onSelectNavItem={setSelectedNavItem}
              verifiedPropertiesCount={propertiesCounts.verified}
              totalPropertiesCount={propertiesCounts.verified + propertiesCounts.needsInput}
              sectionCounts={sectionCounts}
            />
            <Box className={classes.mainContent}>
              {selectedNavItem === 'licenses' ? (
                <LicensesTab
                  classes={classes}
                  verificationStore={verificationStore}
                  documentStore={documentStore}
                />
              ) : selectedNavItem === 'entities' ? (
                <EntitiesTab classes={classes} verificationStore={verificationStore} isInternal={isInternal} />
              ) : selectedNavItem === 'properties' ? (
                <PropertiesTab classes={classes} verificationStore={verificationStore} isInternal={isInternal} />
              ) : selectedNavItem === 'addRecords' ? (
                <AddRecordsTab
                  classes={classes}
                  verificationStore={verificationStore}
                  documentStore={documentStore}
                  borrowerId={borrowerId}
                  onNavigate={setSelectedNavItem}
                />
              ) : (
                <OverviewTab
                  classes={classes}
                  answers={answers}
                  onAnswer={handleAnswer}
                  verificationStore={verificationStore}
                  documentStore={documentStore}
                  borrowerId={borrowerId}
                  isInternal={isInternal}
                  onNavigate={setSelectedNavItem}
                />
              )}
            </Box>
          </Box>
        )}
      </Box>

      {showFooter && selectedTab !== 'chat' && !isLoadingOverview && (
        <TaskFooter
          classes={classes}
          remainingCount={remainingCount}
          onAccept={onAccept ? () => setConfirmAcceptOpen(true) : undefined}
          isSaving={verificationStore?.isUploadingLicense}
          onClose={onClose}
        />
      )}

      <ConfirmDialog
        open={confirmAcceptOpen}
        title="Confirmation"
        body={<Typography>Please confirm you want to mark the task as completed.</Typography>}
        confirmButtonText="Confirm"
        cancelButtonText="Close"
        handleConfirm={handleConfirmAccept}
        handleClose={() => setConfirmAcceptOpen(false)}
        handleCancel={() => setConfirmAcceptOpen(false)}
      />

      {verificationStore && (
        <PDFPreview
          open={verificationStore.isPdfPreviewModalOpen}
          title={verificationStore.pdfTitle}
          data={verificationStore.pdfData}
          headers={{}}
          fileName={verificationStore.pdfTitle}
          onClose={() => {
            verificationStore.pdfTitle = null;
            verificationStore.pdfData = null;
            verificationStore.isPdfPreviewModalOpen = false;
          }}
        />
      )}
    </Box>
  );
});
